/** Small presentational formatters shared across screens. */

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
  ['second', 1],
];

/** "3 minutes ago" / "in 2 days". `null` becomes "never". */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const deltaS = (then - Date.now()) / 1000;
  for (const [unit, secs] of UNITS) {
    if (Math.abs(deltaS) >= secs || unit === 'second') {
      return RELATIVE.format(Math.round(deltaS / secs), unit);
    }
  }
  return 'just now';
}

export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  return Number.isNaN(t) ? '—' : new Date(t).toLocaleString();
}

/** Cents in a 3-letter ISO currency (e.g. 1999, "usd") → "$19.99". */
export function money(cents: number | null | undefined, currency: string | null | undefined): string {
  if (cents == null || !currency) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/** Whole rupiah (Midtrans amounts carry no minor unit) → "Rp 99.000". */
export function rupiah(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount);
}

/** Bytes → "1.4 MB". */
export function fileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`;
}

/**
 * A display is considered online if it polled within ~2.5 sync intervals.
 * The console has no push signal; last-seen is the only liveness we have.
 */
export function isOnline(lastSeenAt: string | null | undefined): boolean {
  if (!lastSeenAt) return false;
  const seen = Date.parse(lastSeenAt);
  return !Number.isNaN(seen) && Date.now() - seen < 1000 * 60 * 3;
}

/** `<input type="datetime-local">` value (local tz, no seconds) from an ISO string. */
export function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const d = new Date(t - new Date().getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
}

/** Inverse of {@link toDatetimeLocal}: local wall-clock value → ISO 8601 (UTC). */
export function fromDatetimeLocal(value: string): string | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}
